/**
 * Normalize and unwrap notification list API payloads.
 * Supports flat { result, totalCount, unreadCount } and ResponseFactory-wrapped shapes.
 */

export const PAGE_SIZE = 10;
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const normalizeNotificationText = (text) => {
	if (typeof text !== 'string') {
		return '';
	}
	const match = text.match(/message\s*:\s*"([^"]*)"/);
	return match ? match[1] : text;
};

export const normalizeNotification = (notification = {}) => ({
	...notification,
	messageId: notification.messageId ?? notification.MessageId ?? '',
	userid: notification.userid || notification.userId || notification.UserId || '',
	type: notification.type ?? notification.Type ?? '',
	title: notification.title ?? notification.Title ?? null,
	text: normalizeNotificationText(notification.text ?? notification.Text ?? ''),
	timestamp: notification.timestamp ?? notification.Timestamp ?? null,
	isRead: Boolean(notification.isRead ?? notification.IsRead),
});

const unwrapPayload = (response) => {
	const topResult = response?.result ?? response?.Result;
	const nested =
		topResult && !Array.isArray(topResult) ? topResult : null;

	if (nested) {
		return {
			items: nested.result ?? nested.Result ?? [],
			totalCount: nested.totalCount ?? nested.TotalCount ?? response.totalCount ?? response.TotalCount ?? 0,
			unreadCount:
				nested.unreadCount ?? nested.UnreadCount ?? response.unreadCount ?? response.UnreadCount ?? 0,
		};
	}

	return {
		items: Array.isArray(topResult) ? topResult : [],
		totalCount: response?.totalCount ?? response?.TotalCount ?? 0,
		unreadCount: response?.unreadCount ?? response?.UnreadCount ?? 0,
	};
};

export const parseNotificationsResponse = (response) => {
	const payload = unwrapPayload(response);
	const rawItems = payload.items;

	return {
		totalCount: Number(payload.totalCount) || 0,
		unreadCount: Number(payload.unreadCount) || 0,
		items: (Array.isArray(rawItems) ? rawItems : []).map(normalizeNotification),
	};
};

export const mergeByMessageId = (current, incoming, prepend = false) => {
	const merged = prepend ? [...incoming, ...current] : [...current, ...incoming];
	const seen = new Set();
	return merged.filter((notification) => {
		if (!notification.messageId || seen.has(notification.messageId)) {
			return false;
		}
		seen.add(notification.messageId);
		return true;
	});
};

export const isOlderThan = (notification, cutoffMs) => {
	if (!notification?.timestamp) {
		return false;
	}
	const timestamp = Date.parse(notification.timestamp);
	return !Number.isNaN(timestamp) && timestamp < cutoffMs;
};
